export type VendorAssessmentDocType = 'SURVEY' | 'EVALUATION' | 'REPORT';
export type VendorAssessmentPurpose = 'INITIAL' | 'PERIODIC' | 'REEVALUATION';
export type VendorAssessmentStatus = 'DRAFT' | 'COMPLETED';
export type VendorAssessmentJudgment =
  | 'PENDING'
  | 'APPROVED'
  | 'CONDITIONAL'
  | 'REJECTED';

export type SurveyResult = 'Y' | 'N' | 'NA' | '';

export interface VendorSurveyItem {
  id: string;
  category: string;
  question: string;
  result: SurveyResult;
  note: string;
}

export interface VendorEvalItem {
  id: string;
  category: string;
  criterion: string;
  maxScore: number;
  score: string;
  note: string;
}

export interface VendorSurveyCriteria {
  kind: 'SURVEY';
  method: string;
  visitPlace: string;
  items: VendorSurveyItem[];
}

export interface VendorEvalCriteria {
  kind: 'EVALUATION';
  items: VendorEvalItem[];
}

export interface VendorReportCriteria {
  kind: 'REPORT';
  background: string;
  method: string;
  findings: string;
  conclusion: string;
  followUp: string;
  relatedSurveyNo: string;
  relatedEvalNo: string;
}

export type VendorAssessmentCriteria =
  | VendorSurveyCriteria
  | VendorEvalCriteria
  | VendorReportCriteria;

export interface VendorAssessment {
  assessmentId: number;
  vendorId: number;
  vendorName?: string | null;
  docType: VendorAssessmentDocType | string;
  purpose?: VendorAssessmentPurpose | string | null;
  assessmentNo?: string | null;
  assessmentDate?: string | null;
  title?: string | null;
  criteriaJson?: string | null;
  scoreText?: string | null;
  judgment?: VendorAssessmentJudgment | string | null;
  summary?: string | null;
  evaluator?: string | null;
  status?: VendorAssessmentStatus | string | null;
  remark?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface VendorAssessmentPayload {
  vendorId: number;
  docType: VendorAssessmentDocType | string;
  purpose?: VendorAssessmentPurpose | string;
  assessmentNo?: string;
  assessmentDate?: string;
  title?: string;
  criteriaJson?: string;
  scoreText?: string;
  judgment?: VendorAssessmentJudgment | string;
  summary?: string;
  evaluator?: string;
  status?: VendorAssessmentStatus | string;
  remark?: string;
}

function newId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

const DEFAULT_SURVEY_ITEMS: Omit<VendorSurveyItem, 'id' | 'result' | 'note'>[] = [
  { category: '일반', question: '사업자등록·허가 등 기본 자격이 확인되는가?' },
  { category: '일반', question: '주요 납품 실적·거래처 현황을 파악했는가?' },
  { category: '품질', question: '품질관리 조직·담당자가 지정되어 있는가?' },
  { category: '품질', question: '검사·시험 설비 및 성적서 관리가 이루어지는가?' },
  { category: '생산', question: '생산능력·납기 대응이 요구 수준에 적합한가?' },
  { category: '생산', question: '공정·설비 관리 상태가 양호한가?' },
  { category: '환경/안전', question: '산업안전·환경 관련 기본 관리가 이루어지는가?' },
];

const DEFAULT_EVAL_ITEMS: Omit<VendorEvalItem, 'id' | 'score' | 'note'>[] = [
  { category: '품질', criterion: '품질시스템·검사체계', maxScore: 20 },
  { category: '품질', criterion: '불량률·시정조치 대응', maxScore: 15 },
  { category: '납기', criterion: '납기준수·납기 대응력', maxScore: 20 },
  { category: '원가', criterion: '가격 경쟁력·원가 성실성', maxScore: 15 },
  { category: '기술', criterion: '기술력·개선 제안 능력', maxScore: 15 },
  { category: '협력', criterion: '의사소통·클레임 대응', maxScore: 15 },
];

export function defaultSurveyCriteria(): VendorSurveyCriteria {
  return {
    kind: 'SURVEY',
    method: 'VISIT',
    visitPlace: '',
    items: DEFAULT_SURVEY_ITEMS.map((item) => ({
      ...item,
      id: newId(),
      result: '',
      note: '',
    })),
  };
}

export function defaultEvalCriteria(): VendorEvalCriteria {
  return {
    kind: 'EVALUATION',
    items: DEFAULT_EVAL_ITEMS.map((item) => ({
      ...item,
      id: newId(),
      score: '',
      note: '',
    })),
  };
}

export function defaultReportCriteria(): VendorReportCriteria {
  return {
    kind: 'REPORT',
    background: '',
    method: '',
    findings: '',
    conclusion: '',
    followUp: '',
    relatedSurveyNo: '',
    relatedEvalNo: '',
  };
}

export function defaultCriteriaForDocType(
  docType: VendorAssessmentDocType
): VendorAssessmentCriteria {
  switch (docType) {
    case 'EVALUATION':
      return defaultEvalCriteria();
    case 'REPORT':
      return defaultReportCriteria();
    case 'SURVEY':
    default:
      return defaultSurveyCriteria();
  }
}

export function parseAssessmentCriteria(
  docType: VendorAssessmentDocType,
  raw?: string | null
): VendorAssessmentCriteria {
  const fallback = defaultCriteriaForDocType(docType);
  if (!raw?.trim()) return fallback;
  try {
    const parsed = JSON.parse(raw) as Partial<VendorAssessmentCriteria> & { kind?: string };
    if (docType === 'SURVEY') {
      const base = fallback as VendorSurveyCriteria;
      if (parsed.kind && parsed.kind !== 'SURVEY') return base;
      const p = parsed as Partial<VendorSurveyCriteria>;
      return {
        kind: 'SURVEY',
        method: p.method || 'VISIT',
        visitPlace: p.visitPlace || '',
        items:
          Array.isArray(p.items) && p.items.length > 0
            ? p.items.map((item) => ({
                id: item.id || newId(),
                category: item.category || '',
                question: item.question || '',
                result: (item.result as SurveyResult) || '',
                note: item.note || '',
              }))
            : base.items,
      };
    }
    if (docType === 'EVALUATION') {
      const base = fallback as VendorEvalCriteria;
      if (parsed.kind && parsed.kind !== 'EVALUATION') return base;
      const p = parsed as Partial<VendorEvalCriteria>;
      return {
        kind: 'EVALUATION',
        items:
          Array.isArray(p.items) && p.items.length > 0
            ? p.items.map((item) => ({
                id: item.id || newId(),
                category: item.category || '',
                criterion: item.criterion || '',
                maxScore: Number(item.maxScore) > 0 ? Number(item.maxScore) : 10,
                score: item.score ?? '',
                note: item.note || '',
              }))
            : base.items,
      };
    }
    const base = fallback as VendorReportCriteria;
    if (parsed.kind && parsed.kind !== 'REPORT') return base;
    const p = parsed as Partial<VendorReportCriteria>;
    return {
      kind: 'REPORT',
      background: p.background || '',
      method: p.method || '',
      findings: p.findings || '',
      conclusion: p.conclusion || '',
      followUp: p.followUp || '',
      relatedSurveyNo: p.relatedSurveyNo || '',
      relatedEvalNo: p.relatedEvalNo || '',
    };
  } catch {
    return fallback;
  }
}

export function calcEvalScoreText(items: VendorEvalItem[]): string {
  let earned = 0;
  let max = 0;
  let filled = 0;
  for (const item of items) {
    max += item.maxScore || 0;
    const n = Number(item.score);
    if (item.score.trim() !== '' && Number.isFinite(n)) {
      earned += n;
      filled += 1;
    }
  }
  if (filled === 0 || max <= 0) return '';
  const pct = Math.round((earned / max) * 100);
  const grade = pct >= 90 ? 'A' : pct >= 80 ? 'B' : pct >= 70 ? 'C' : 'D';
  return `${earned}/${max} (${pct}%, ${grade})`;
}

export function surveyMethodLabel(method?: string | null): string {
  switch ((method || '').toUpperCase()) {
    case 'VISIT':
      return '방문조사';
    case 'DOCUMENT':
      return '서면조사';
    case 'REMOTE':
      return '비대면(화상)';
    default:
      return method || '-';
  }
}

export function surveyResultLabel(result?: string | null): string {
  switch ((result || '').toUpperCase()) {
    case 'Y':
      return '적합';
    case 'N':
      return '미흡';
    case 'NA':
      return '해당없음';
    default:
      return '-';
  }
}

export function vendorAssessmentDocTypeLabel(docType?: string | null): string {
  switch ((docType || '').toUpperCase()) {
    case 'SURVEY':
      return '조사표';
    case 'EVALUATION':
      return '평가표';
    case 'REPORT':
      return '조사보고서';
    default:
      return docType || '-';
  }
}

export function vendorAssessmentPurposeLabel(purpose?: string | null): string {
  switch ((purpose || '').toUpperCase()) {
    case 'INITIAL':
      return '신규선정';
    case 'PERIODIC':
      return '정기';
    case 'REEVALUATION':
      return '재평가';
    default:
      return purpose || '-';
  }
}

export function vendorAssessmentStatusLabel(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'DRAFT':
      return '작성중';
    case 'COMPLETED':
      return '완료';
    default:
      return status || '-';
  }
}

export function vendorAssessmentJudgmentLabel(judgment?: string | null): string {
  switch ((judgment || '').toUpperCase()) {
    case 'PENDING':
      return '판정대기';
    case 'APPROVED':
      return '적합';
    case 'CONDITIONAL':
      return '조건부';
    case 'REJECTED':
      return '부적합';
    default:
      return judgment || '-';
  }
}

export function vendorAssessmentDocHint(docType?: string | null): string {
  switch ((docType || '').toUpperCase()) {
    case 'SURVEY':
      return '신규·실사 시 사실 확인용 체크리스트입니다. 점수보다 적합/미흡 확인에 초점을 둡니다.';
    case 'EVALUATION':
      return '선정·정기·재평가용 배점표입니다. 항목 점수를 합산해 등급·판정에 반영합니다.';
    case 'REPORT':
      return '조사·평가 결과를 종합하는 서술형 보고서입니다. 관련 조사표·평가표 번호를 함께 남기세요.';
    default:
      return '';
  }
}
